import React from 'react';
import { Button, IconButton, Box } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { useUnitOverride, uploadUnitAsset, updateUnitContent } from '../../services/unitOverridesService';

const PAGE_ID = 'm3_u2_x1_kuehlkreis';

const images = [
  { key: 'kk_fbh', src: '/assets/drawable/hk_fbh.PNG', alt: 'Kühlkreis Schema', label: 'Kühlkreis' }
];

const textBlocks = [
  {
    id: 'block_anlagenelemente',
    heading: 'Anlagenelemente (Kühlkreis)',
    body: 'Kühlkreis mit Kaltwasserpumpe, Regelventil, Frostschutzthermostat, Vorlauffühler und Rücklauffühler.'
  },
  {
    id: 'block_regelung',
    heading: 'Regelung (Kühlkreis)',
    body: 'Kaltwasservorlauftemperaturregelung mit Sollwertvorgabe, optionaler Rücklaufbegrenzung, Pumpensteuerung und Frostschutzüberwachung.'
  },
  {
    id: 'block_funktionsbeschreibung',
    heading: 'Funktionsbeschreibung (Kühlkreis)',
    body:
      'Der Kühlkreis dient der Temperierung von Räumen oder Prozessen mittels Kaltwasser. Die Kaltwassertemperatur (B1) wird auf einen festen Sollwert geregelt ' +
      '(z.B. 6°C Vorlauf für Kühldecken, 12°C Vorlauf für Lüftungsanlagen). Dieser Sollwert wird im Programm oder über die GLT vorgegeben. ' +
      'Die Kaltwasserpumpe (P1) fördert das Medium zu den Verbrauchern.'
  },
  {
    id: 'block_ventilsteuerung',
    heading: 'Ventilsteuerung',
    body:
      'Abhängig von der Regelabweichung des Vorlauffühlers (B1) zum Sollwert wird das Kaltwasserventil (Y1) angesteuert, um die Kaltwassermenge zu den Verbrauchern zu modulieren oder den Kaltwassererzeuger zu beeinflussen. ' +
      'Bei Erreichen des Sollwerts schließt das Ventil bzw. reduziert seine Öffnung.'
  },
  {
    id: 'block_freigabe',
    heading: 'Freigabe und Abschaltung',
    body:
      'Die Freigabe des Kühlkreises erfolgt oft über einen externen Kontakt (z.B. Sommerbetrieb aktiv) oder eine Zeitsteuerung. ' +
      'Bei Abschaltung wird die Pumpe (P1) gestoppt und das Ventil (Y1) geschlossen.'
  },
  {
    id: 'block_ueberwachung',
    heading: 'Überwachung (Kühlkreis)',
    body:
      'Ein Frostschutzthermostat oder -fühler (K1) im Kaltwasservorlauf oder an kritischen Stellen überwacht die Temperatur, um ein Einfrieren des Systems zu verhindern. ' +
      'Bei Unterschreitung eines Grenzwertes (z.B. 2°C) wird die Kaltwasserpumpe (P1) abgeschaltet und das Ventil (Y1) geschlossen. Eine Störung wird gemeldet. ' +
      'Zusätzlich kann die Pumpenfunktion (Störmeldung) und die Ventilstellung überwacht werden.'
  },
  {
    id: 'block_notbedien',
    heading: 'Notbedienebene (Kühlkreis)',
    body: 'Analog zum Heizkreis kann eine Notbedienebene vorhanden sein, um Pumpe und Ventil manuell zu steuern.'
  }
];

const M3_U2_X1_Kuehlkreis: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM3, canUploadImagesM3 } = useAuth();
  const canEditTexts = canEditTextsM3;
  const canUploadImages = canUploadImagesM3;
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const mergedBlocks = React.useMemo(() => 
    textBlocks.map(tb => ({
      ...tb,
      body: override?.texts?.[tb.id] || tb.body
    })),
    [override]
  );

  const fullText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = React.useCallback(async (blockId: string, body: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: body } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = React.useCallback(async (key: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, key, file, token);
      alert('Bild aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '3.2.1 Kühlkreis', actions: ttsBtn },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          alt: img.alt,
          label: 'Bild einfügen',
          onReplace: canUploadImages ? (file: File) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map(tb => React.createElement(EditableTextBlock, {
          key: tb.id,
          pageId: PAGE_ID,
          blockId: tb.id,
          heading: tb.heading,
          body: tb.body,
          onSave: (body: string) => handleTextSave(tb.id, body)
        })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m3_u2_x99_quiz') }, 'Zum Quiz')
        )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } })
  );
};

export default M3_U2_X1_Kuehlkreis;

